"use client";

import { useEffect } from "react";

export default function SmoothScrollToForm() {
  useEffect(() => {
    // Disable the browser's default smooth scroll so it can't fight the rAF
    // animation below. This is an INLINE style on <html>, which outranks the
    // `html { scroll-behavior: smooth }` rule in globals.css — so it must be
    // undone on unmount. Leaving it set made every in-page CTA across the site
    // teleport instead of animating for the rest of the session.
    // Check if we should trigger smooth scroll
    // 1. Either from clicking register button (with #auto-scroll hash)
    // 2. Or from refreshing class registration detail page directly
    const shouldScroll = window.location.hash === '#auto-scroll' || window.location.pathname.includes('/class-registration/');

    if (shouldScroll) {
      const previousScrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      const restoreScrollBehavior = () => {
        document.documentElement.style.scrollBehavior = previousScrollBehavior;
      };

      // Remove hash if it exists to keep URL clean
      if (window.location.hash === '#auto-scroll') {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      }
      
      // Prevent any pending scroll by listening to scroll events
      let isAnimating = false;
      let animationComplete = 0;
      
      const scrollListener = (e: Event) => {
        // Block any scroll attempts during animation and shortly after
        if (isAnimating || (animationComplete && Date.now() - animationComplete < 500)) {
          e.preventDefault();
          e.stopPropagation();
        }
      };
      
      window.addEventListener('scroll', scrollListener, { passive: false });

      function triggerSmoothScroll() {
        let attempts = 0;
        const maxAttempts = 80;
        
        const checkAndScroll = () => {
          attempts++;
          const target = document.getElementById('registration-form');
          
          if (target && target.offsetParent !== null) {
            isAnimating = true;
            
            const headerOffset = 300; // Much larger offset to scroll less and keep image visible
            const elementPosition = target.getBoundingClientRect().top;
            const startingY = window.scrollY;
            const targetY = startingY + elementPosition - headerOffset;
            
            // Skip animation if already at target
            if (Math.abs(targetY - startingY) < 10) {
              isAnimating = false;
              animationComplete = Date.now();
              return;
            }
            
            const duration = 1200; 
            let start: number | null = null;
            let frameId: number | null = null;

            function easeInOutCubic(t: number) {
              return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
            }

            function animateScroll(timestamp: number) {
              if (!start) start = timestamp;
              const progress = timestamp - start;
              const percent = Math.min(progress / duration, 1);
              const ease = easeInOutCubic(percent);
              
              window.scrollTo(0, Math.round(startingY + (targetY - startingY) * ease));

              if (progress < duration) {
                frameId = window.requestAnimationFrame(animateScroll);
              } else {
                isAnimating = false;
                animationComplete = Date.now();
                if (frameId) cancelAnimationFrame(frameId);
              }
            }

            frameId = window.requestAnimationFrame(animateScroll);
          } else if (attempts < maxAttempts) {
            setTimeout(checkAndScroll, 150);
          } else {
            isAnimating = false;
            animationComplete = Date.now();
          }
        };
        
        checkAndScroll();
      }
      
      // Start scrolling after form has time to load and render
      setTimeout(triggerSmoothScroll, 200);

      // Cleanup on unmount
      return () => {
        window.removeEventListener('scroll', scrollListener);
        restoreScrollBehavior();
      };
    }
  }, []);

  return null;
}
